import { ACCOUNT_DELETION_COPY } from '@frontend/constants';
import { useState } from '@frontend/react';
import { useAuthSlice } from '@frontend/redux';
import type { CommonModalProps } from '@frontend/web-react';
import { Button, LinkButton, Modal, Text, TextInput, YStack, showToast } from '@frontend/web-react';
import { useAnalytics, useDeleteClientMutation, useRetrieveClientQuery } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';

export const DeleteAccountModal = ({ isOpen, onClose }: CommonModalProps) => {
  const { clearAuth } = useAuthSlice();
  const { track } = useAnalytics();
  const { data: client } = useRetrieveClientQuery();
  const [deleteClient, { isLoading: isDeleteAccountLoading }] = useDeleteClientMutation();
  const [confirmationText, setConfirmationText] = useState('');
  const [displayError, setDisplayError] = useState(false);

  const handleDeleteAccount = async () => {
    if (!isConfirmationValid) {
      setDisplayError(true);
      return;
    }

    if (!client?.id) return;

    track({
      data: {
        clientId: client.id,
      },
      event: 'deleteClientConfirmed',
      topic: 'auth',
    });

    await handleMutation({
      mutation: async () => deleteClient({ clientId: client.id }),
      onSuccess: () => {
        onClose?.();
        showToast({
          title: 'Account deleted.',
          variant: 'info',
        });
        // Give the user time to read the toast
        setTimeout(() => {
          clearAuth();
        }, 3000);
      },
      showErrorToast: false,
      showSuccessToast: false,
    });
  };

  const isConfirmationValid = confirmationText.trim().toLowerCase() === 'duck it';

  return (
    <Modal
      bodyClassName="bg-trueWhite"
      headerGap="none"
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title="Delete Your Account"
      titlesAlignment="start"
    >
      <YStack gapY="lg">
        <YStack gapY="md">
          <YStack gapY="sm">
            {ACCOUNT_DELETION_COPY.split('\n').map((p, i) => (
              <Text key={i} type="p-md">
                {p}
              </Text>
            ))}
          </YStack>
          <TextInput
            error={displayError ? 'Please type "duck it" to confirm' : undefined}
            onChange={(value: string) => setConfirmationText(value)}
            placeholder="Type: duck it"
            value={confirmationText}
          />
          <YStack gapY="md">
            <Button
              // eslint-disable-next-line react/jsx-no-leaked-render
              disabled={displayError && !isConfirmationValid}
              fullWidth
              isLoading={isDeleteAccountLoading}
              label="Delete My Account Forever"
              onClick={handleDeleteAccount}
              variant="destructive"
            />
            <LinkButton label="Cancel" onClick={onClose} py="md" />
          </YStack>
        </YStack>
      </YStack>
    </Modal>
  );
};
